/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { Text, useText } from '@adobe-commerce/elsie/i18n';
import { VComponent, classes, getFormValues } from '@adobe-commerce/elsie/lib';
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useRef, useState } from 'preact/compat';

import '@/cart/components/EstimateShipping/EstimateShipping.css';

export interface EstimateShippingProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  countryField?: VNode<HTMLAttributes<HTMLInputElement>>;
  destinationText?: string;
  estimateButton?: VNode<HTMLAttributes<HTMLButtonElement>>;
  estimated?: boolean;
  loading: boolean;
  onEstimate?: (formData: any) => void;
  price: VNode<HTMLAttributes<HTMLSpanElement>>;
  priceExcludingTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
  priceIncludingTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
  stateField?: VNode<HTMLAttributes<HTMLInputElement>>;
  taxExcluded?: boolean;
  taxIncluded?: boolean;
  zipField?: VNode<HTMLAttributes<HTMLInputElement>>;
}

export const EstimateShipping: FunctionComponent<EstimateShippingProps> = ({
  countryField,
  destinationText,
  estimateButton,
  estimated,
  loading,
  onEstimate,
  price,
  priceExcludingTax,
  priceIncludingTax,
  stateField,
  taxExcluded,
  taxIncluded,
  zipField,
}) => {
  const estimateFormRef = useRef<HTMLFormElement>(null);
  const [showEstimateForm, setShowEstimateForm] = useState(true);
  const [estimateField, setEstimateField] = useState<'zip' | 'state'>('zip');

  const dictionary = useText({
    editZipAction: 'Cart.EstimateShipping.editZipAction',
    destinationLinkAriaLabel: 'Cart.EstimateShipping.destinationLinkAriaLabel',
    shippingLabel: 'Cart.EstimateShipping.label',
    zipPlaceholder: 'Cart.EstimateShipping.zipPlaceholder',
  });

  const onDestinationLinkClick = (e: Event) => {
    e.preventDefault();
    setShowEstimateForm((show) => !show);
  };

  const onAlternateFieldClick = (e: Event) => {
    e.preventDefault();
    setShowEstimateForm(true);
    setEstimateField((field) => (field === 'zip' ? 'state' : 'zip'));
  };

  const onSubmitEstimate = (e: Event) => {
    e.preventDefault();
    setShowEstimateForm(false);

    const formData = getFormValues(estimateFormRef.current!);

    onEstimate?.(formData);
  };

  const destinationAriaLabel = dictionary.destinationLinkAriaLabel.replace('{destination}', destinationText);

  return (  
    <div
      data-testid="estimate-shipping"
      className={classes([
        'cart-estimate-shipping',
        ['cart-estimate-shipping--loading', loading],
      ])}
    >
      <span className={'cart-estimate-shipping__label'}>
        {estimated ? (
          destinationText ? (
            <>
              <Text id="Cart.EstimateShipping.estimatedDestination" />
              &nbsp;
              <a
                className={'cart-estimate-shippingLink'}
                role="button"
                href=""
                onClick={onDestinationLinkClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    onDestinationLinkClick(event);
                  }
                }}
                tabIndex={0}
                aria-label={destinationAriaLabel}
                data-testid="shipping-destination-link"
              >
                {destinationText}
              </a>
            </>
          ) : (
            <Text id="Cart.EstimateShipping.estimated" />
          )
        ) : (
          <Text id="Cart.EstimateShipping.label" />
        )}
      </span>

      <VComponent node={price} className={'cart-estimate-shipping__price'} />

      {/* Edit estimate form */}
      {estimated && (
        <>
          <div className={classes(['cart-estimate-shipping__caption'])}>
            <a
              href="#"
              className={'cart-estimate-shipping__link'}
              onClick={onAlternateFieldClick}
              data-testid="shipping-alternate-field-link"
            >
              {estimateField === 'zip' ? (
                <Text id="Cart.EstimateShipping.alternateField.state" />
              ) : (
                <Text id="Cart.EstimateShipping.alternateField.zip" />
              )}
            </a>
          </div>

          <form
            className={classes([
              'cart-estimate-shipping--edit',
              ['cart-estimate-shipping--hide', !showEstimateForm],
            ])}
            ref={estimateFormRef}
            data-testid="shipping-estimate-form"
          >
            {countryField && (
              <VComponent
                node={countryField}
                className={classes(['cart-estimate-shipping--country'])}
              />
            )}
            {estimateField === 'state'
              ? stateField && (
                  <VComponent
                    node={stateField}
                    className={classes(['cart-estimate-shipping--state'])}
                  />
                )
              : zipField && (
                  <VComponent
                    node={zipField}
                    className={classes(['cart-estimate-shipping--zip'])}
                  />
                )}
            {estimateButton && (
              <VComponent
                node={estimateButton}
                className={classes(['cart-estimate-shipping--action'])}
                onClick={onSubmitEstimate}
                type="submit"
              />
            )}
          </form>
        </>
      )}

      {taxIncluded && (
        <div
          data-testid="shipping-tax-included"
          className={classes(['cart-estimate-shipping__caption'])}
        >
          <span>
            {priceIncludingTax} <Text id="Cart.EstimateShipping.withTaxes" />
          </span>
        </div>
      )}

      {taxExcluded ? (
        <div
          data-testid="shipping-tax-included-excluded"
          className={classes(['cart-estimate-shipping__caption'])}
        >
          <span>
            {priceExcludingTax} <Text id="Cart.EstimateShipping.withoutTaxes" />
          </span>
        </div>
      ) : undefined}
    </div>
  );
};
